import * as React from "react";
import { Control, UseFieldArrayReturn, useWatch } from "react-hook-form";
import { Stack, Title, useMantineTheme } from "@mantine/core";
import { IconChecklist, IconFileUpload, IconHeartRateMonitor, IconLinkPlus } from "@tabler/icons-react";

import { UseCaseCVMContext } from "./UseCaseCVMContext";
import { Workflow } from "./Workflow/WorkflowCVM";
import {
  removeNode,
  addEdgeViaPort,
  removeEdge,
  updateAction,
  workflowInitialState,
  workflowReducer,
} from "./Workflow/WorkflowUtils";
import ActionRouter from "./Workflow/Action/ActionRouter";
import AddViaPort from "./Workflow/Add/AddViaPort";
import AddViaEdge from "./Workflow/Add/AddViaEdge";
import AddViaGroup from "./Workflow/Add/AddViaGroup";
import { UseCaseType } from "@supportinitiative/common";

const options = [
  { label: "Reconciliation", name: "RECONCILIATION", icon: <IconChecklist /> },
  { label: "Monitoring", name: "MONITORING", icon: <IconHeartRateMonitor /> },
  { label: "Dataload", name: "DATALOAD", icon: <IconFileUpload /> },
  { label: "Serial Group", name: "SERIAL_GROUP", icon: <IconLinkPlus /> },
  { label: "Parallel Group", name: "PARALLEL_GROUP", icon: <IconLinkPlus /> },
];

const groupOptions = [{ label: "Dataload", name: "DATALOAD", icon: <IconFileUpload /> }];

function OrchestratorCVM({
  name,
  control,
  reconsArray,
}: {
  name: `definition.orchestrator`;
  control: Control<any>;
  reconsArray: UseFieldArrayReturn<UseCaseType, "definition.recons", "key">;
}) {
  const theme = useMantineTheme();
  const { state: useCaseFormState } = React.useContext(UseCaseCVMContext);

  const [workflowState, workflowDispatch] = React.useReducer(workflowReducer, workflowInitialState);

  const workflow = useWatch({
    control,
    name: `${name}.workflow`,
  });

  // Add through edge plus button
  // Add through port drop
  //    Node create
  //    Edge create
  // Add through group
  // delete node

  /*   if (!workflow) {
    const {
      nodes: defaultNodes,
      edges: defaultEdges,
      actions: defaultActions,
    } = getDefaultWorkflow();
    useCaseFormState.setValue(`${name}.workflow`, {
      nodes: defaultNodes,
      edges: defaultEdges,
      actions: defaultActions,
    });
  } */

  const cancel = () => {
    workflowDispatch({
      type: "RESET",
      value: {
        action: null,
        via: null,
      },
    });
  };

  const save = (values: any) => {
    const nodes = workflow && workflow.nodes ? workflow.nodes : [],
      edges = workflow && workflow.edges ? workflow.edges : [],
      actions = workflow && workflow.actions ? workflow.actions : [];
    const newWorkflow = updateAction(values, nodes, edges, actions);
    useCaseFormState.setValue(`${name}.workflow`, newWorkflow);
    workflowDispatch({
      type: "RESET",
      value: {
        action: null,
        via: null,
      },
    });
  };

  return (
    <>
      <Stack
        style={{
          marginTop: theme.spacing.xs,
          padding: theme.spacing.xs,
          width: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
          }}
        >
          <Title
            mb="xs"
            order={5}
            c="white"
            py={0}
            px={theme.spacing.xxs}
            style={{
              backgroundColor: theme.colors.emerald[0],
            }}
          >
            Workflow
          </Title>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "flex-start",
          }}
        >
          <Workflow
            allNodes={workflow && workflow.nodes ? workflow.nodes : []}
            allEdges={workflow && workflow.edges ? workflow.edges : []}
            disabled={useCaseFormState.actionOptions.disableAll}
            selectedEdge={workflowState.edge ? workflowState.edge : undefined}
            selectedNode={workflowState.node ? workflowState.node : undefined}
            onAddNode={(edge) => {
              workflowDispatch({
                type: "SET",
                value: {
                  action: "ADD",
                  via: "EDGE",
                  edge: edge,
                },
              });
            }}
            onPortDragEnd={(fromNodeId, toNodeId) => {
              if (toNodeId) {
                const nodes = workflow && workflow.nodes ? workflow.nodes : [],
                  edges = workflow && workflow.edges ? workflow.edges : [],
                  actions = workflow && workflow.actions ? workflow.actions : [];

                const newWorkflow = addEdgeViaPort({ from: fromNodeId, to: toNodeId }, nodes, edges, actions);
                useCaseFormState.setValue(`${name}.workflow`, newWorkflow);
              } else {
                workflowDispatch({
                  type: "SET",
                  value: {
                    action: "ADD",
                    via: "PORT",
                    port: {
                      from: fromNodeId,
                      to: toNodeId,
                    },
                  },
                });
              }
            }}
            onGroupAdd={(node) => {
              // console.log("onGroupAdd", node);
              workflowDispatch({
                type: "SET",
                value: {
                  action: "ADD",
                  via: "GROUP",
                  node: node,
                },
              });
            }}
            onCanvasClick={() => {
              // console.log("onCanvasClick");
              // setSelectedEdge(undefined);
            }}
            onDeleteEdge={(edge) => {
              // console.log("onDeleteEdge");
              const nodes = workflow && workflow.nodes ? workflow.nodes : [],
                edges = workflow && workflow.edges ? workflow.edges : [],
                actions = workflow && workflow.actions ? workflow.actions : [];

              const newWorkflow = removeEdge(edge, nodes, edges, actions);
              workflowDispatch({
                type: "RESET",
                value: {
                  action: null,
                  via: null,
                },
              });
              // console.log("onDeleteEdge workflow", newWorkflow);
              useCaseFormState.setValue(`${name}.workflow`, newWorkflow);
            }}
            onDeleteNode={(node) => {
              const nodes = workflow && workflow.nodes ? workflow.nodes : [],
                edges = workflow && workflow.edges ? workflow.edges : [],
                actions = workflow && workflow.actions ? workflow.actions : [];

              const newWorkflow = removeNode(node, nodes, edges, actions);
              workflowDispatch({
                type: "RESET",
                value: {
                  action: null,
                  via: null,
                },
              });
              // console.log("onDeleteNode workflow", newWorkflow);
              useCaseFormState.setValue(`${name}.workflow`, newWorkflow);
            }}
            onNodeClick={(node) => {
              // console.log("onNodeClick", node);
              if (
                node.data?.type.toUpperCase() === "GROUP" ||
                node.data?.type.toUpperCase() === "SERIAL_GROUP" ||
                node.data?.type.toUpperCase() === "PARALLEL_GROUP"
              ) {
                // do no trigger node click for group
              } else {
                workflowDispatch({
                  type: "SET",
                  value: {
                    action: "VIEW",
                    via: "NODE",
                    node: node,
                  },
                });
              }
            }}
            onEdgeClick={(edge) => {
              // console.log("onEdgeClick", edge);
              workflowDispatch({
                type: "SET",
                value: {
                  action: "VIEW",
                  via: "EDGE",
                  edge: edge,
                },
              });
            }}
          />
        </div>
      </Stack>

      {workflowState && workflowState.action === "ADD" && workflowState.via === "EDGE" && workflowState.edge && (
        <AddViaEdge
          name={`${name}.workflow`}
          edge={workflowState.edge}
          workflowDispatch={workflowDispatch}
          nodes={workflow && workflow.nodes ? workflow.nodes : []}
          edges={workflow && workflow.edges ? workflow.edges : []}
          actions={workflow && workflow.actions ? workflow.actions : []}
          reconsArray={reconsArray}
          options={options}
          fromOrchestrator
        />
      )}

      {workflowState && workflowState.action === "ADD" && workflowState.via === "PORT" && workflowState.port && (
        <AddViaPort
          name={`${name}.workflow`}
          port={workflowState.port}
          workflowDispatch={workflowDispatch}
          nodes={workflow && workflow.nodes ? workflow.nodes : []}
          edges={workflow && workflow.edges ? workflow.edges : []}
          actions={workflow && workflow.actions ? workflow.actions : []}
          reconsArray={reconsArray}
          options={options}
          fromOrchestrator
        />
      )}

      {workflowState && workflowState.action === "ADD" && workflowState.via === "GROUP" && workflowState.node && (
        <AddViaGroup
          name={`${name}.workflow`}
          node={workflowState.node}
          workflowDispatch={workflowDispatch}
          nodes={workflow && workflow.nodes ? workflow.nodes : []}
          edges={workflow && workflow.edges ? workflow.edges : []}
          actions={workflow && workflow.actions ? workflow.actions : []}
          reconsArray={reconsArray}
          options={groupOptions}
          fromOrchestrator
        />
      )}

      {workflowState && workflowState.action === "VIEW" && workflowState.via === "NODE" && workflowState.node && (
        <ActionRouter
          node={workflowState.node}
          save={save}
          cancel={cancel}
          from="ORCHESTRATOR"
          actions={workflow && workflow.actions ? workflow.actions : []}
        />
      )}
    </>
  );
}

export default OrchestratorCVM;
